import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { ConstantsService } from '../../fidelidade/ConstantsService';
import { NotificationService } from '../../services/notification.service';
import { LibraryGroupVM, ProductCatalogItemVM } from '../produtos-v2.models';
import { ProdutosV2FacadeService } from '../produtos-v2.facade';

type AdditionalCatalogFilterV2 = 'all' | 'exclusive' | 'shared' | 'unlinked';

@Component({
  selector: 'app-tela-catalogo-adicionais-v2',
  templateUrl: './tela-catalogo-adicionais-v2.component.html',
  styleUrls: ['./tela-catalogo-adicionais-v2.component.scss']
})
export class TelaCatalogoAdicionaisV2Component implements OnInit, OnDestroy {
  empresa: any;
  loading = true;
  errorMessage = '';
  search = '';
  filter: AdditionalCatalogFilterV2 = 'all';
  items: LibraryGroupVM[] = [];
  products: ProductCatalogItemVM[] = [];
  productPickerOpen = false;
  productSearch = '';

  private empresaSubscription?: Subscription;

  constructor(
    private constantsService: ConstantsService,
    private facade: ProdutosV2FacadeService,
    private router: Router,
    private notificationService: NotificationService
  ) {}

  ngOnInit(): void {
    this.empresaSubscription = this.constantsService.empresa$.subscribe((empresa) => {
      if (!empresa?.catalogo?.id) return;
      this.empresa = empresa;
      void this.load();
    });
  }

  ngOnDestroy(): void {
    this.empresaSubscription?.unsubscribe();
  }

  @HostListener('document:keydown.escape')
  closePickerOnEscape(): void {
    this.closeProductPicker();
  }

  get canEdit(): boolean {
    return !this.empresa?.id || !this.empresa?.catalogo?.compartilhado;
  }

  get exclusiveCount(): number {
    return this.items.filter((item) => item.sharedCount === 1).length;
  }

  get sharedCount(): number {
    return this.items.filter((item) => item.sharedCount > 1).length;
  }

  get unlinkedCount(): number {
    return this.items.filter((item) => item.sharedCount === 0).length;
  }

  get filteredItems(): LibraryGroupVM[] {
    const term = this.normalize(this.search);
    return this.items.filter((item) => {
      const matchesTerm = !term
        || this.normalize(item.name).includes(term)
        || this.normalize(item.description).includes(term)
        || (item.sharedProductNames || []).some((name) => this.normalize(name).includes(term));
      if (!matchesTerm) return false;
      if (this.filter === 'exclusive') return item.sharedCount === 1;
      if (this.filter === 'shared') return item.sharedCount > 1;
      if (this.filter === 'unlinked') return item.sharedCount === 0;
      return true;
    });
  }

  get filteredProducts(): ProductCatalogItemVM[] {
    const term = this.normalize(this.productSearch);
    return this.products
      .filter((product) => !term
        || this.normalize(product.name).includes(term)
        || this.normalize(product.category).includes(term))
      .sort((left, right) => left.name.localeCompare(right.name, 'pt-BR'));
  }

  async load(): Promise<void> {
    this.loading = true;
    this.errorMessage = '';
    try {
      this.products = await this.facade.carregueCatalogo(this.empresa);
      this.items = await this.facade.carregueBiblioteca(this.empresa.catalogo);
    } catch (error: any) {
      this.errorMessage = error?.message || 'Não foi possível carregar os adicionais do catálogo.';
    } finally {
      this.loading = false;
    }
  }

  setFilter(filter: AdditionalCatalogFilterV2): void {
    this.filter = filter;
  }

  openAdditional(item: LibraryGroupVM): void {
    void this.router.navigate(['/admin/adicionais-v2', item.id], {
      queryParams: { produto: item.sourceProductId || item.productId || null }
    });
  }

  openProductPicker(): void {
    if (!this.canEdit) {
      this.notificationService.showError('A estrutura dos adicionais é controlada pelo catálogo da rede.');
      return;
    }
    this.productSearch = '';
    this.productPickerOpen = true;
  }

  closeProductPicker(): void {
    this.productPickerOpen = false;
  }

  createForProduct(product: ProductCatalogItemVM): void {
    if (!product.id) return;
    this.productPickerOpen = false;
    void this.router.navigate(['/admin/produtos-v2'], {
      queryParams: {
        produtoDrawer: product.id,
        drawerSurface: 'group-detail',
        drawerGroupMode: 'create',
        drawerTab: 'adicionais'
      }
    });
  }

  goToProducts(): void {
    void this.router.navigate(['/admin/produtos-v2']);
  }

  usageLabel(item: LibraryGroupVM): string {
    if (item.sharedCount === 0) return 'Sem vínculo';
    if (item.sharedCount === 1) return 'Exclusivo';
    return `Compartilhado · ${item.sharedCount} produtos`;
  }

  usageSupport(item: LibraryGroupVM): string {
    const names = item.sharedProductNames || [];
    if (!names.length) return item.sharedCount === 0 ? 'Ainda não usado em produtos' : 'Produto não identificado';
    if (names.length <= 2) return names.join(', ');
    return `${names.slice(0, 2).join(', ')} +${names.length - 2}`;
  }

  requirementLabel(item: LibraryGroupVM): string {
    return Number(item.min || 0) > 0 ? 'Obrigatório' : 'Opcional';
  }

  trackByAdditional(index: number, item: LibraryGroupVM): any {
    return item.id || index;
  }

  trackByProduct(index: number, item: ProductCatalogItemVM): any {
    return item.id || index;
  }

  private normalize(value: any): string {
    return String(value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .trim();
  }
}
